import { useEffect, useState } from 'react';
import { Save, AlertCircle, RefreshCw } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetHomepage, adminGetProducts, adminUpdateHomepage } from '../../services/api';

const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2.5 outline-none transition-colors placeholder-zinc-600";
const labelCls = "block font-inter text-xs text-zinc-400 uppercase tracking-wider mb-1.5";

export default function AdminHomepage() {
  const { token } = useAdminAuth();
  const [form, setForm] = useState({
    hero_headline: '',
    hero_subheadline: '',
    hero_background_url: '',
    hero_cta_primary_text: '',
    hero_cta_primary_href: '',
    promo_banner_text: '',
    promo_banner_active: true,
    featured_product_slugs: [],
  });
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([adminGetHomepage(token), adminGetProducts(token)])
      .then(([hp, prods]) => {
        setForm({
          hero_headline: hp.hero_headline || '',
          hero_subheadline: hp.hero_subheadline || '',
          hero_background_url: hp.hero_background_url || '',
          hero_cta_primary_text: hp.hero_cta_primary_text || '',
          hero_cta_primary_href: hp.hero_cta_primary_href || '',
          promo_banner_text: hp.promo_banner_text || '',
          promo_banner_active: hp.promo_banner_active !== false,
          featured_product_slugs: hp.featured_product_slugs || [],
        });
        setAllProducts(prods.filter(p => p.status === 'active'));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const set = k => e => setForm(f => ({
    ...f,
    [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const toggleFeatured = (slug) => {
    setForm(f => ({
      ...f,
      featured_product_slugs: f.featured_product_slugs.includes(slug)
        ? f.featured_product_slugs.filter(s => s !== slug)
        : [...f.featured_product_slugs, slug],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await adminUpdateHomepage(token, form);
      setSuccess('Homepage settings saved! Changes are live.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} data-testid="homepage-editor-form">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">HOMEPAGE EDITOR</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Control what visitors see on the homepage</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" className="font-inter text-xs text-zinc-500 hover:text-orange-500 transition-colors">
            Preview Site →
          </a>
          <button
            data-testid="save-homepage-button"
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">
          <AlertCircle size={15} />{error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3 mb-4">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hero Section */}
        <div className="bg-[#1a1a1a] border border-white/10 p-5">
          <h2 className="font-bebas text-xl text-white tracking-wider mb-5 flex items-center gap-2">
            <span className="w-1.5 h-5 bg-orange-500" />
            HERO SECTION
          </h2>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Headline (Line 1)</label>
              <input
                data-testid="hero-headline-input"
                className={inputCls}
                value={form.hero_headline}
                onChange={set('hero_headline')}
                placeholder="BUILT FOR BATTLE."
              />
              <p className="font-inter text-[10px] text-zinc-600 mt-1">Displays in large Bebas Neue font. Keep it punchy.</p>
            </div>
            <div>
              <label className={labelCls}>Subheadline</label>
              <textarea
                data-testid="hero-subheadline-input"
                rows={3}
                className={`${inputCls} resize-none`}
                value={form.hero_subheadline}
                onChange={set('hero_subheadline')}
                placeholder="Ultra compact fighting suppressors..."
              />
            </div>
            <div>
              <label className={labelCls}>Background Image URL</label>
              <input
                data-testid="hero-background-input"
                className={inputCls}
                value={form.hero_background_url}
                onChange={set('hero_background_url')}
                placeholder="https://..."
              />
              {form.hero_background_url && (
                <div className="mt-2 h-24 bg-[#0f0f0f] overflow-hidden">
                  <img src={form.hero_background_url} alt="" className="w-full h-full object-cover opacity-50" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>CTA Button Text</label>
                <input className={inputCls} value={form.hero_cta_primary_text} onChange={set('hero_cta_primary_text')} placeholder="SHOP SUPPRESSORS" />
              </div>
              <div>
                <label className={labelCls}>CTA Button Link</label>
                <input className={inputCls} value={form.hero_cta_primary_href} onChange={set('hero_cta_primary_href')} placeholder="/suppressors" />
              </div>
            </div>
          </div>
        </div>

        {/* Promo Banner */}
        <div className="bg-[#1a1a1a] border border-white/10 p-5">
          <h2 className="font-bebas text-xl text-white tracking-wider mb-5 flex items-center gap-2">
            <span className="w-1.5 h-5 bg-orange-500" />
            PROMO BANNER
          </h2>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Banner Text</label>
              <input
                data-testid="banner-text-input"
                className={inputCls}
                value={form.promo_banner_text}
                onChange={set('promo_banner_text')}
                placeholder="FREE SHIPPING ON ORDERS $450+"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="banner-active-toggle"
                type="checkbox"
                checked={form.promo_banner_active}
                onChange={set('promo_banner_active')}
                className="w-4 h-4 accent-orange-500"
              />
              <span className="font-inter text-sm text-zinc-300">Show promo banner</span>
            </label>
          </div>

          {/* Preview */}
          <div className={`mt-5 p-3 text-center transition-all ${form.promo_banner_active ? 'bg-orange-500' : 'bg-zinc-800 opacity-50'}`}>
            <p className="font-bebas text-base text-white tracking-widest">{form.promo_banner_text || 'Banner preview'}</p>
          </div>
        </div>

        {/* Featured Products */}
        <div className="lg:col-span-2 bg-[#1a1a1a] border border-white/10 p-5">
          <h2 className="font-bebas text-xl text-white tracking-wider mb-2 flex items-center gap-2">
            <span className="w-1.5 h-5 bg-orange-500" />
            FEATURED PRODUCTS
          </h2>
          <p className="font-inter text-xs text-zinc-500 mb-4">Select up to 6 products to display in the Featured Products section on the homepage.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="featured-products-selector">
            {allProducts.map(p => {
              const isSelected = form.featured_product_slugs.includes(p.slug);
              return (
                <button
                  key={p._id}
                  type="button"
                  data-testid={`feature-toggle-${p.slug}`}
                  onClick={() => toggleFeatured(p.slug)}
                  className={`flex items-start gap-2 p-3 border text-left transition-all ${
                    isSelected
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="w-10 h-10 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                    <img src={p.image} alt="" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                  <div className="min-w-0">
                    <p className={`font-inter text-xs font-medium leading-tight ${isSelected ? 'text-orange-400' : 'text-zinc-300'}`}>{p.name}</p>
                    <p className="font-inter text-[10px] text-zinc-600 mt-0.5">${p.price?.toFixed(2)}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="font-inter text-xs text-zinc-600 mt-3">
            {form.featured_product_slugs.length} of {allProducts.length} products selected
          </p>
        </div>
      </div>
    </form>
  );
}
